/**
 * Static graph for the admin AI file-import flowchart.
 * AI schemas come from the same builders the runner uses — no hand-copied JSON.
 */

import { z } from 'zod';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  buildProjectFileBatchMergeStructuredOutputRootJsonSchema,
  PROJECT_FILE_BATCH_MERGE_STRUCTURED_OUTPUT_NAME,
} from '@shared/helpers/project-file-batch-merge-json-schema.helpers';
import { AI_MODEL_USE_CASES } from '@shared/models/ai/ai-model-use-cases.model';
import {
  FFU_PIPE_ID,
  FFU_SINGLE_CALL_PIPE_ID,
  type ProjectFilePipeId,
} from '@shared/models/project-file-pipe.model';
import {
  classifierDocumentLabelsSchema,
  projectFileBatchAggregatedOutputSchema,
  projectFileBatchJoinedBlueprintExtractionSchema,
  projectFileClassificationEnvelopeSchema,
  projectFileClassificationTypeSchema,
} from '@shared/models/project-file-batch.model';
import { ffuOutputFactsSchema } from '@shared/models/ffu-output.model';
import { projectFileExtractionOutputV1Schema } from '@shared/models/project-file-extraction.model';
import { siteModelSchema } from '@shared/models/project-file-site-model.model';
import { getProjectFileBatchAiChainPromptSections } from '@shared/helpers/project-file-batch-ai-chain-prompt-catalog.helpers';
import {
  buildJoinedBlueprintExtractionRootJsonSchema,
  PROJECT_FILE_BLUEPRINT_JOINED_EXTRACTION_SCHEMA_NAME,
} from '@shared/helpers/project-file-blueprint-joined-extraction-json-schema.helpers';
import {
  buildDrawingFootprintJsonSchema,
  PROJECT_FILE_DRAWING_FOOTPRINT_SCHEMA_NAME,
  PROJECT_FILE_SITE_MODEL_SCHEMA_NAME,
  buildSiteModelJsonSchema,
} from '@shared/helpers/project-file-extraction-json-schema.helpers';
import {
  buildDrawingScaleJsonSchema,
  PROJECT_FILE_DRAWING_SCALE_SCHEMA_NAME,
} from '@shared/helpers/project-file-drawing-scale-prompt.helpers';
import {
  buildFfuChatMessageJsonSchema,
  FFU_CHAT_MESSAGE_SCHEMA_NAME,
} from '@shared/helpers/ffu-chat-message-prompt.helpers';

export const PIPELINE_FLOW_NODE_IDS = [
  'upload',
  'classify',
  'drawings',
  'raster',
  'align',
  'footprints',
  'scale',
  'confirm',
  'extract',
  'joined',
  'merge',
  'site',
  'output',
  'project',
  'chat',
  'done',
] as const;

export type PipelineFlowNodeId = string;

export type PipelineFlowKind = 'ai' | 'code' | 'gate' | 'io';

/** `skip` is the path taken when the target's predecessor is gated off. */
export type PipelineFlowEdgeKind = 'next' | 'skip';

/** Gate on a step: `label` fits a node chip, `detail` is the full rule. */
export type PipelineFlowCondition = {
  label: string;
  detail: string;
};

/** Background box around steps that share one concurrency scope. */
export type PipelineFlowLane = {
  id: string;
  label: string;
  col: number;
  row: number;
  cols: number;
  rows: number;
};

export type PipelineFlowSchemaRef = {
  id: string;
  label: string;
  structuredOutputName?: string;
  schema: Record<string, unknown>;
};

export type PipelineFlowPort = {
  id: string;
  label: string;
  summary: string;
  schemas?: readonly PipelineFlowSchemaRef[];
};

export type PipelineFlowNode = {
  id: string;
  title: string;
  shortTitle: string;
  phaseLabel: string;
  kind: PipelineFlowKind;
  purpose: string;
  parallelism?: string;
  /** Short concurrency chip, e.g. "∥ per file". */
  fanOut?: string;
  condition?: PipelineFlowCondition;
  useCaseLabel?: string;
  defaultModel?: string;
  inputs: readonly PipelineFlowPort[];
  outputs: readonly PipelineFlowPort[];
  col: number;
  row: number;
};

export type PipelineFlowEdge = {
  from: string;
  to: string;
  kind: PipelineFlowEdgeKind;
  label?: string;
};

export type PipelineFlowGraph = {
  nodes: readonly PipelineFlowNode[];
  edges: readonly PipelineFlowEdge[];
  lanes: readonly PipelineFlowLane[];
};

/** Batch steps between the per-file lane and confirm, run one after another. */
const PRE_CONFIRM_CHAIN = ['align', 'footprints', 'scale', 'confirm'] as const;

/** `next` edges from each id to the one after it. */
const chainEdges = (ids: readonly string[]): PipelineFlowEdge[] =>
  ids.slice(1).flatMap((to, i) => {
    const from = ids[i];
    return from === undefined ? [] : [{ from, to, kind: 'next' as const }];
  });

const zodToJsonSchema = (schema: z.ZodType): Record<string, unknown> => {
  const { $schema: _ignored, ...rest } = z.toJSONSchema(schema) as Record<
    string,
    unknown
  > & { $schema?: string };
  return rest;
};

type SectionMap = Record<
  string,
  ReturnType<typeof getProjectFileBatchAiChainPromptSections>[number]
>;

const catalogSchemas = (
  sections: SectionMap,
  sectionId: string,
): readonly PipelineFlowSchemaRef[] => {
  const section = sections[sectionId];
  if (!section) return [];
  return section.structuredOutputs.map((item) => ({
    id: item.id,
    label: item.label,
    structuredOutputName: item.structuredOutputName,
    schema: item.schema,
  }));
};

const catalogContextPort = (
  sections: SectionMap,
  sectionId: string,
): PipelineFlowPort => {
  const section = sections[sectionId];
  return {
    id: `${sectionId}-context`,
    label: 'Sent with the request',
    summary: (section?.contextSent ?? []).join(' '),
  };
};

const modelUseCaseMeta = (id: keyof typeof AI_MODEL_USE_CASES) => {
  const uc = AI_MODEL_USE_CASES[id];
  return {
    useCaseLabel: uc.label,
    defaultModel: uc.defaultModel,
  };
};

const aiCallNote =
  'Each call is appended to batch.aiCalls (step, model, tokens, duration, outcome).';

/** Batch step between alignment and the scale check; see {@link PRE_CONFIRM_CHAIN}. */
const footprintsNode = (): PipelineFlowNode => ({
  id: 'footprints',
  title: 'Building footprints',
  shortTitle: 'Footprints',
  phaseLabel: 'Batch · classifying',
  kind: 'ai',
  purpose:
    'One call per plan drawing, including a roof plan. The crop is cut from the page raster. Polygons come back as fractions of that crop. A failed crop is logged and left without a footprint. ' +
    aiCallNote,
  parallelism:
    'Starts after alignment. Files one after another; plans of one file in parallel. The batch is still classifying, so a restart traces every plan again. Nothing before confirm reads the footprints: the scale check and the final output do, and the site model through the scale check.',
  fanOut: 'files 1 by 1 · plans ∥',
  condition: {
    label: 'if plans',
    detail:
      'Only plan drawings with a page box and a page raster. A batch without plans makes no call.',
  },
  ...modelUseCaseMeta('projectFileDrawingFootprint'),
  inputs: [
    {
      id: 'footprints-in',
      label: 'Plan crop',
      summary:
        'PNG of the drawing region (long edge about 2048). Sections are not traced.',
    },
  ],
  outputs: [
    {
      id: 'footprints-ai',
      label: 'AI structured output',
      summary:
        'Polygons with building keys, cutouts, printed areas, printed name, and site number. Stored as drawing.footprint.',
      schemas: [
        {
          id: 'drawing-footprint',
          label: 'Footprint on one plan',
          structuredOutputName: PROJECT_FILE_DRAWING_FOOTPRINT_SCHEMA_NAME,
          schema: buildDrawingFootprintJsonSchema(),
        },
      ],
    },
  ],
  col: 5,
  row: 0,
});

/**
 * Multi-step pipe: upload → per file (classify → drawing search → raster, or
 * straight on without drawings) → align → footprints → scale → confirm →
 * extract ∥ joined → merge → site model → final output → project → chat
 * message → done.
 */
export const getProjectFilePipelineFlowGraph = (): PipelineFlowGraph => {
  const sections: SectionMap = Object.fromEntries(
    getProjectFileBatchAiChainPromptSections().map((s) => [s.id, s]),
  );
  const classifySection = sections.classifier;
  const mergeSection = sections['batch-merge'];
  const extractSchemas = projectFileClassificationTypeSchema.options.flatMap(
    (type) =>
      catalogSchemas(sections, `extraction-${type}`).map((s) => ({
        ...s,
        id: `extraction-${type}`,
        label: type,
      })),
  );

  const nodes: PipelineFlowNode[] = [
    {
      id: 'upload',
      title: 'Create batch + store blobs',
      shortTitle: 'Upload',
      phaseLabel: 'Batch · collecting',
      kind: 'io',
      purpose:
        'POST flattens zip archives and client-walked folders, creates the batch and one file row per inner file, and stores each buffer in durable blob storage (filesystem locally, GCS hosted) with blob_ref. The runner starts in the background. A restart reloads bytes from that store and re-kicks non-terminal batches owned by an admin.',
      inputs: [
        {
          id: 'upload-files',
          label: 'UploadedProjectFileInput[]',
          summary:
            'Per file: fileName (relative path), mimeType, sizeBytes, buffer. Zip members are expanded first. Also userId, organizationId, optional projectId, optional createProject.',
        },
      ],
      outputs: [
        {
          id: 'upload-rows',
          label: 'Batch + file rows',
          summary:
            'pipeId is ffu. Batch state collecting → classifying. Each file starts queued. queued → preprocessing is a no-op; preprocessing → classifying is the next tick.',
        },
      ],
      col: 0,
      row: 0,
    },
    {
      id: 'classify',
      title: classifySection?.title ?? 'Classifier',
      shortTitle: 'Classify',
      phaseLabel: classifySection?.phaseLabel ?? 'Per file · classifying',
      kind: 'ai',
      purpose:
        'One call per file over the whole file. Segments, whole-file document_kind, and name/description hints. Each segment lists pages: every 1-based page it sits on. Empty for an image, or when the segment cannot be placed. page_hint stays a free-text location. Later PDF reads send only those pages. Building ids are canonicalized onto the file. ' +
        aiCallNote,
      parallelism:
        'Files fan out in parallel (Promise.all per runner tick); each tick waits for the slowest file before the next state step. The classify call and any drawing search that follows it share the file state classifying, so a restart before classified repeats both.',
      fanOut: '∥ per file',
      ...modelUseCaseMeta('projectFileClassifier'),
      inputs: [
        catalogContextPort(sections, 'classifier'),
        {
          id: 'classify-blob',
          label: 'File bytes',
          summary:
            'Stored buffer attached as vision input_image for png/jpeg/webp/gif, else input_file. User text starts with the original filename plus the classifier instruction.',
        },
      ],
      outputs: [
        {
          id: 'classify-ai',
          label: 'AI structured output',
          summary:
            'segments[], suggested project name and description, document_kind and confidence.',
          schemas: catalogSchemas(sections, 'classifier'),
        },
        {
          id: 'classify-persisted',
          label: 'Persisted on project_files',
          summary:
            'Envelope per segment (confirmed is still false) and classifier document labels.',
          schemas: [
            {
              id: 'classification-envelope',
              label: 'Classification envelope',
              schema: zodToJsonSchema(projectFileClassificationEnvelopeSchema),
            },
            {
              id: 'classifier-document-labels',
              label: 'Document labels',
              schema: zodToJsonSchema(classifierDocumentLabelsSchema),
            },
          ],
        },
      ],
      col: 1,
      row: 0,
    },
    {
      id: 'drawings',
      title: 'Drawing search',
      shortTitle: 'Drawings',
      phaseLabel: 'Per file · classifying',
      kind: 'ai',
      purpose:
        'Finds each drawing on the sheets: page, bbox, building ids, kind, plan view, storey role, plan numbers, and printed scale. A PDF is cut to the blueprint pages (same page count, other pages blank, poppler pdfseparate/pdfunite) and reported pages are mapped back to the original. The whole file is sent when those pages are missing or the cut fails. An empty answer for a file with blueprint hints is asked once more with a retry suffix. ' +
        aiCallNote,
      parallelism:
        'Runs right after the classify call of the same file, inside its classifying tick, so files search in parallel.',
      fanOut: '∥ per file',
      condition: {
        label: 'if blueprint',
        detail:
          'Only when the file has a blueprint segment or document_kind technical_drawing_set, and is a PDF or a raster image. Other files go straight to classified.',
      },
      ...modelUseCaseMeta('projectFileDrawingDetection'),
      inputs: [
        {
          id: 'drawings-in',
          label: 'File bytes + classifier hints',
          summary:
            'Blueprint pages of the stored PDF when the classifier listed them, otherwise the whole file. Plus building id/label hints so ids stay aligned.',
        },
      ],
      outputs: [
        {
          id: 'drawings-ai',
          label: 'AI structured output',
          summary:
            'drawings[]: one row per ritning. An empty list despite a blueprint segment is kept as a failure on the file. Classification still continues.',
          schemas: catalogSchemas(sections, 'drawing-detection'),
        },
      ],
      col: 2,
      row: 1,
    },
    {
      id: 'raster',
      title: 'Poppler page raster',
      shortTitle: 'Raster',
      phaseLabel: 'Per file · classifying',
      kind: 'code',
      purpose:
        'Rasterizes each page the drawing search named, once. PDF pages go through poppler (pdftoppm), about 200 DPI with a long-edge cap. A raster image is stored as its own page and does not call poppler. Not its own state: it finishes before the file becomes classified, and a restart repeats it with the drawing search.',
      parallelism:
        'Per file after its drawing search. PDF rasters share one server-wide queue, so two files never run poppler at the same time.',
      fanOut: 'PDFs one at a time',
      condition: {
        label: 'if drawings',
        detail:
          'Only after a drawing search, and only for the pages it named. Nothing is rasterized when it found no drawings.',
      },
      inputs: [
        {
          id: 'raster-in',
          label: 'Detected page numbers',
          summary:
            '1-based pages from drawings[].page, plus the original file bytes.',
        },
      ],
      outputs: [
        {
          id: 'raster-out',
          label: 'Page rasters + drawing rows',
          summary:
            'pageRasters (webp) on the file. Each drawing keeps bbox and rect in the page frame. blobRef on the drawing stays null — there is no JPEG crop per ritning.',
        },
      ],
      col: 3,
      row: 1,
    },
    {
      id: 'align',
      title: 'Alias rewrite + plan alignment',
      shortTitle: 'Align',
      phaseLabel: 'Batch · classifying',
      kind: 'code',
      purpose:
        'When every file is classified, failed, or cancelled: the batch fails if no file classified, or if createProject is set and every segment is unknown. Otherwise aliased building ids are rewritten across the batch, then the best plan crop per storey is aligned (page position and ink). No model call; without plan drawings nothing is aligned. A failure is logged and the batch still advances. Runs again on resume while the batch is still classifying. Nothing before confirm reads the alignment: it is used by the final output.',
      parallelism:
        'Batch barrier: waits for every file. Alignment, footprints, and the scale check run one after another in the same runner tick.',
      inputs: [
        {
          id: 'align-in',
          label: 'Classifications + page rasters',
          summary:
            'Building labels from every file, and the plan drawings with their page rasters. A legacy drawing blobRef is still readable.',
        },
      ],
      outputs: [
        {
          id: 'align-out',
          label: 'Stable ids + planAlignment',
          summary:
            'One building id per physical building where the alias rule applies. planAlignment / planAlignments written on the drawings.',
        },
      ],
      col: 4,
      row: 0,
    },
    footprintsNode(),
    {
      id: 'scale',
      title: 'Plan scale check',
      shortTitle: 'Scale',
      phaseLabel: 'Batch · classifying',
      kind: 'ai',
      purpose:
        'One call per traced plan whose scale readings disagree (scale bar, printed ratio, other sheets of the building). Sheets that agree are not asked. The model picks a candidate; the code measured the candidates. A failed call leaves the pending judgement and the output falls back to printed areas. ' +
        aiCallNote,
      parallelism:
        'Starts after every footprint, since it needs the traced outline. Files one after another; sheets of one file in parallel. A restart while classifying asks again.',
      fanOut: 'files 1 by 1 · sheets ∥',
      condition: {
        label: 'if scales disagree',
        detail:
          'Only plans that got a footprint and have at least two scale candidates. Sheets whose readings agree are not asked.',
      },
      ...modelUseCaseMeta('projectFileDrawingScale'),
      inputs: [
        {
          id: 'scale-in',
          label: 'Outlined plan crop',
          summary:
            'The footprint crop with solid outlines drawn, plus the real-world size each candidate scale would give those outlines.',
        },
      ],
      outputs: [
        {
          id: 'scale-ai',
          label: 'AI structured output',
          summary:
            'choice, confidence, and evidence. Stored as drawing.scaleJudgement.',
          schemas: [
            {
              id: 'drawing-scale',
              label: 'Scale judgement',
              structuredOutputName: PROJECT_FILE_DRAWING_SCALE_SCHEMA_NAME,
              schema: buildDrawingScaleJsonSchema(),
            },
          ],
        },
      ],
      col: 6,
      row: 0,
    },
    {
      id: 'confirm',
      title: 'Auto-confirm segments',
      shortTitle: 'Confirm',
      phaseLabel: 'Batch · awaiting_confirmation',
      kind: 'gate',
      purpose:
        'Stamps confirmed: true on every classification segment, then moves the batch to extracting. No model call. The runner confirms on its own in the same run, so the batch does not wait for a person; the confirm endpoint does the same thing when called first. Confirm reads only classifications.',
      inputs: [
        {
          id: 'confirm-in',
          label: 'Classifications per file',
          summary:
            'Files that reached classified. Empty classification lists are skipped. The batch already failed if no file classified, or if createProject is set and every segment is unknown.',
        },
      ],
      outputs: [
        {
          id: 'confirm-out',
          label: 'Confirmed segments',
          summary:
            'Same envelopes with data.confirmed = true. Batch state → extracting. Joined blueprint work may start.',
        },
      ],
      col: 7,
      row: 0,
    },
    {
      id: 'extract',
      title: 'Per-segment reading',
      shortTitle: 'Extract',
      phaseLabel: 'Per file · extracting',
      kind: 'ai',
      purpose:
        'One call per classifier segment. unknown is skipped. A title_block the classifier already read is copied and not called again. Drawings and text documents use drawingSegmentExtraction and textSegmentExtraction (same use case, different file detail). A PDF call is sent only that segment’s pages; segments that share a page list share one upload. The whole file is sent for an image, a segment without pages, or a failed cut. ' +
        aiCallNote,
      parallelism:
        'In parallel with the whole-building reading. Files in parallel. Segments of one file in parallel, with the first call of each cache group (same pages, type, and step) warming the prefix before its siblings.',
      fanOut: '∥ per file · ∥ per segment',
      ...modelUseCaseMeta('projectFileSegmentExtraction'),
      inputs: [
        {
          id: 'extract-in',
          label: 'Confirmed segment + file bytes',
          summary:
            'Classifier type, pages, page hint, evidence, building hints, and upload context. Blueprint adds the geometry stem. The attachment is the segment excerpt when pages were listed.',
        },
      ],
      outputs: [
        {
          id: 'extract-ai',
          label: 'AI structured output per type',
          summary:
            'One schema per classificationType. Expand a type to see the Responses json_schema.',
          schemas: extractSchemas,
        },
        {
          id: 'extract-persisted',
          label: 'Persisted extraction_output',
          summary:
            'Merged object on the file. File → done only if at least one segment is ok; otherwise failed.',
          schemas: [
            {
              id: 'extraction-output-v1',
              label: 'extraction_output',
              schema: zodToJsonSchema(projectFileExtractionOutputV1Schema),
            },
          ],
        },
      ],
      col: 8,
      row: 0,
    },
    {
      id: 'joined',
      title: 'Whole-building reading',
      shortTitle: 'Buildings',
      phaseLabel: 'Batch · extracting → consolidating',
      kind: 'ai',
      purpose:
        'One call per building group (the admin cost row “Whole-building reading”). Groups by classifier building_instance_id; blueprint segments without an id share one ungrouped group. Each file is the pages of that building’s blueprint segments and its renderings. A rendering without a building id goes to every building that file has drawings of. A segment without pages sends the whole file. Volume only — per-storey numbers stay on the segment read. The promise is in memory; consolidation awaits it, or runs it again after a restart. ' +
        aiCallNote,
      parallelism:
        'Started when the batch enters extracting and not awaited there, so it runs beside per-file extraction and may still run when the batch is consolidating. Building groups run in parallel; groups attaching the same pages wait for one warm-up call.',
      fanOut: '∥ per building',
      condition: {
        label: 'if blueprints',
        detail:
          'Only when some file has a blueprint segment. No group means no call and an empty result for the merge.',
      },
      ...modelUseCaseMeta('projectFileBlueprintExtraction'),
      inputs: [
        {
          id: 'joined-in',
          label: 'Blueprint groups + file bytes',
          summary:
            'Each file cut to that building’s blueprint and rendering pages, with filename and building label. The model reads the drawings itself.',
        },
      ],
      outputs: [
        {
          id: 'joined-ai',
          label: 'AI structured output',
          summary:
            'One buildings[] row per group call. Failures are stored beside successes.',
          schemas: [
            {
              id: 'joined-ai-root',
              label: 'Root object { buildings }',
              structuredOutputName:
                PROJECT_FILE_BLUEPRINT_JOINED_EXTRACTION_SCHEMA_NAME,
              schema: buildJoinedBlueprintExtractionRootJsonSchema(),
            },
          ],
        },
        {
          id: 'joined-persisted',
          label: 'Row stored for the merger',
          summary:
            'Successes become aggregatedOutput.joinedBlueprintExtractions. Failures go to joinedBlueprintExtractionFailures.',
          schemas: [
            {
              id: 'joined-persisted-row',
              label: 'Joined extraction row',
              schema: zodToJsonSchema(
                projectFileBatchJoinedBlueprintExtractionSchema,
              ),
            },
          ],
        },
      ],
      col: 8,
      row: 1,
    },
    {
      id: 'merge',
      title: mergeSection?.title ?? 'Merge labels + buildings',
      shortTitle: 'Merge',
      phaseLabel: mergeSection?.phaseLabel ?? 'Batch · consolidating',
      kind: 'ai',
      purpose:
        'One text call for the batch. Heuristic labels, buildings, and facts when the call fails or there is nothing to merge. This answer is not the batch output yet. ' +
        aiCallNote,
      parallelism:
        'Waits for every file to finish extracting and for the whole-building reading. Scope project-file-batch-label-merge|<batchId>. Re-run on resume while the batch is consolidating.',
      condition: {
        label: 'if anything to merge',
        detail:
          'Called only when there are name or description hints, building snapshots, whole-building rows, or design-fact candidates. Otherwise, or when the call fails, heuristics stand in. The batch fails before this step if no file produced an extraction.',
      },
      ...modelUseCaseMeta('projectFileLabelConsolidation'),
      inputs: [
        catalogContextPort(sections, 'batch-merge'),
        {
          id: 'merge-payload',
          label: 'Consolidation payload',
          summary:
            'name_hints, description_hints, building snapshots, joined blueprint rows, and design_fact_candidates (one row per extracted leaf).',
        },
      ],
      outputs: [
        {
          id: 'merge-ai',
          label: 'AI structured output',
          summary:
            'Suggested names, consolidated buildings, compositions, and extracted design facts. Kept so a later replay of the code can skip this call.',
          schemas: catalogSchemas(sections, 'batch-merge'),
        },
      ],
      col: 9,
      row: 0,
    },
    {
      id: 'site',
      title: 'Site model',
      shortTitle: 'Site model',
      phaseLabel: 'Batch · consolidating',
      kind: 'ai',
      purpose:
        'One call after the merge. Every plan page goes in whole, with where each drawing sits, its scale, the storey counts the extraction read, and the buildings the merge read. Out: every house and every storey outline in metres, in one frame. A failed read is logged and the batch continues without a site model. ' +
        aiCallNote,
      parallelism:
        'Sequential after the merge; page uploads run in parallel before the one call.',
      condition: {
        label: 'if plan pages',
        detail:
          'Only when some page holds a plan drawing. Otherwise no call and no site model.',
      },
      ...modelUseCaseMeta('projectFileSiteModel'),
      inputs: [
        {
          id: 'site-in',
          label: 'Plan page rasters + merge facts',
          summary:
            'PNG per plan page (long edge 2048), drawing boxes, and the buildings and storey counts from the merge. No call when no page holds a plan.',
        },
      ],
      outputs: [
        {
          id: 'site-ai',
          label: 'AI structured output',
          summary:
            'buildings[] with storey indexes (0 is the entrance) and polygons. Stored as batch.siteModel. Unknown building ids are dropped.',
          schemas: [
            {
              id: 'site-model-ai',
              label: 'Site model answer',
              structuredOutputName: PROJECT_FILE_SITE_MODEL_SCHEMA_NAME,
              schema: buildSiteModelJsonSchema(),
            },
            {
              id: 'site-model-stored',
              label: 'Stored site model',
              schema: zodToJsonSchema(siteModelSchema),
            },
          ],
        },
      ],
      col: 10,
      row: 0,
    },
    {
      id: 'output',
      title: 'Layout, entrance index, final output',
      shortTitle: 'Output',
      phaseLabel: 'Batch · consolidating',
      kind: 'code',
      purpose:
        'Code after the model calls. Layout places drawings and fills storeys. Storey index 0 is the entrance plan: a named entrance, or the one storey the model marked at 0; if neither names it, the gap fill is Plan 10. Parts of one connected house become one building from the site model. toFfuOutputFacts (with the site model) is aggregatedOutput.extractedDesignFacts. toFinalFfuOutputFacts, stored as finalOutput: parts share the house count, storeys below the entrance get an elevation, program shares and facade/roof materials are normalised, and every storey gets a footprint. Storey shapes: site model storeys win; without a site model, the per-drawing footprint and then storey_footprints from the blueprint segment read are the fallback. Then the per-step token log is written.',
      inputs: [
        {
          id: 'output-in',
          label: 'Merge answer + site model + files',
          summary:
            'Joined rows, design-fact candidates, drawings (with footprints and scale judgements), storey_footprints from the blueprint segment reads, requirements, and batch.siteModel when the read succeeded.',
        },
      ],
      outputs: [
        {
          id: 'output-facts',
          label: 'Final facts and extractedDesignFacts',
          summary:
            'Same FfuOutputFacts schema. The final facts are the design normalisation of the tree written on aggregatedOutput.extractedDesignFacts; they are stored as finalOutput. Conflicts, joined rows, and the raw merge facts stay on the aggregated object only.',
          schemas: [
            {
              id: 'ffu-output-facts',
              label: 'FfuOutputFacts',
              schema: zodToJsonSchema(ffuOutputFactsSchema),
            },
            {
              id: 'aggregated-output',
              label: 'ProjectFileBatchAggregatedOutput',
              schema: zodToJsonSchema(projectFileBatchAggregatedOutputSchema),
            },
          ],
        },
      ],
      col: 11,
      row: 0,
    },
    {
      id: 'project',
      title: 'Create project',
      shortTitle: 'Project',
      phaseLabel: 'Batch · consolidating',
      kind: 'code',
      purpose:
        'The project is copied from the baseline template with the final output as its design, the site is looked up with one web-search call (ffuProjectLocation, recorded in aiCalls), and storeys the editor would reject are rescued. Runs before the batch moves to done, so the user sees the project only when the whole chain has finished. A failure is written on the batch and the batch still finishes.',
      condition: {
        label: 'if createProject',
        detail:
          'Only when the batch was started with createProject and has no project yet. An admin retry rebuilds the output from the stored model answers and runs this step again.',
      },
      inputs: [
        {
          id: 'project-in',
          label: 'finalOutput',
          summary: 'The final output written in the output step.',
        },
      ],
      outputs: [
        {
          id: 'project-out',
          label: 'Project',
          summary:
            'A new project attached to the batch (projectId), with one history entry for the FFU import.',
        },
      ],
      col: 12,
      row: 1,
    },
    {
      id: 'chat',
      title: 'Chat message',
      shortTitle: 'Chat',
      phaseLabel: 'Batch · consolidating',
      kind: 'ai',
      purpose:
        'One text call for every project created, after the project row exists and before the batch is attached. Code builds the facts: the opening line, overview, requirements and choices with their reasons, each value once and empty subjects left out. The model writes the project’s first agent chat message from them, with the merge’s value conflicts: opening line, Overview, Requirements, and “Assumptions & decisions” last. A failed call gets the message code writes from the same facts, so the import never fails on it. ' +
        aiCallNote,
      parallelism:
        'Sequential inside project creation. Prompt cache key ffu-chat-message|<batchId>.',
      ...modelUseCaseMeta('projectFileLabelConsolidation'),
      inputs: [
        {
          id: 'chat-in',
          label: 'Message facts',
          summary:
            'Opening line, overview lines, requirement lines, choices with their reasons, the merge conflicts other than names, and each building key with its short name.',
        },
      ],
      outputs: [
        {
          id: 'chat-ai',
          label: 'AI structured output',
          summary:
            'message: the whole Markdown message, posted as the first message of the project’s agent chat.',
          schemas: [
            {
              id: 'ffu-chat-message',
              label: 'Chat message',
              structuredOutputName: FFU_CHAT_MESSAGE_SCHEMA_NAME,
              schema: buildFfuChatMessageJsonSchema(),
            },
          ],
        },
      ],
      col: 13,
      row: 1,
    },
    {
      id: 'done',
      title: 'Batch done',
      shortTitle: 'Done',
      phaseLabel: 'Batch · done',
      kind: 'io',
      purpose:
        'Terminal success. Admin and chat read the aggregated tree; a created project reads finalOutput. aiCalls is summed per step on the batch page (AI cost per step: calls, USD, tokens, cached, over 272k input) and as one header total. List price is gpt-6.1-sol; other models stay unpriced. Failed and interrupted are the other terminals.',
      inputs: [
        {
          id: 'done-in',
          label: 'Aggregated output + finalOutput',
          summary:
            'Written in the output step. The batch fails earlier when no file produced an extraction.',
        },
      ],
      outputs: [
        {
          id: 'done-out',
          label: 'Snapshot to clients',
          summary:
            'ProjectFileBatchSnapshot over REST and project_file_batch_update, including aiCalls, siteModel, and finalOutput.',
        },
      ],
      col: 14,
      row: 0,
    },
  ];

  const edges: PipelineFlowEdge[] = [
    { from: 'upload', to: 'classify', kind: 'next' },
    { from: 'classify', to: 'drawings', kind: 'next' },
    { from: 'drawings', to: 'raster', kind: 'next' },
    { from: 'raster', to: 'align', kind: 'next' },
    { from: 'classify', to: 'align', kind: 'skip', label: 'no drawings' },
    ...chainEdges(PRE_CONFIRM_CHAIN),
    { from: 'confirm', to: 'extract', kind: 'next' },
    { from: 'confirm', to: 'joined', kind: 'next' },
    { from: 'extract', to: 'merge', kind: 'next' },
    { from: 'joined', to: 'merge', kind: 'next' },
    { from: 'merge', to: 'site', kind: 'next' },
    { from: 'site', to: 'output', kind: 'next' },
    { from: 'output', to: 'project', kind: 'next' },
    { from: 'project', to: 'chat', kind: 'next' },
    { from: 'chat', to: 'done', kind: 'next' },
    { from: 'output', to: 'done', kind: 'skip', label: 'no createProject' },
  ];

  const lanes: PipelineFlowLane[] = [
    {
      id: 'per-file-classify',
      label: 'Per file · files in parallel',
      col: 1,
      row: 0,
      cols: 3,
      rows: 2,
    },
    {
      id: 'batch-pre-confirm',
      label: 'Batch · after every file · one by one',
      col: 4,
      row: 0,
      cols: PRE_CONFIRM_CHAIN.length - 1,
      rows: 1,
    },
    {
      id: 'extract-parallel',
      label: 'In parallel',
      col: 8,
      row: 0,
      cols: 1,
      rows: 2,
    },
  ];

  return { nodes, edges, lanes };
};

export const findPipelineFlowNode = (
  graph: PipelineFlowGraph,
  id: string,
): PipelineFlowNode | undefined => graph.nodes.find((n) => n.id === id);

/** AI step when the pipe has one, otherwise the first node. */
export const defaultPipeFlowNodeId = (graph: PipelineFlowGraph): string =>
  graph.nodes.find((node) => node.kind === 'ai')?.id ??
  graph.nodes[0]?.id ??
  '';

/**
 * Selected node, or the pipe's own default when the id belongs to another
 * pipe's graph.
 */
export const resolvePipeFlowNode = (
  graph: PipelineFlowGraph,
  selectedId: string | null,
): PipelineFlowNode | undefined => {
  const found = graph.nodes.find((node) => node.id === selectedId);
  if (found) return found;
  return graph.nodes.find((node) => node.id === defaultPipeFlowNodeId(graph));
};

const getSingleCallPipeFlowGraph = (): PipelineFlowGraph => {
  const schema = buildProjectFileBatchMergeStructuredOutputRootJsonSchema();
  const nodes: PipelineFlowNode[] = [
    {
      id: 'upload',
      title: 'Store every source file',
      shortTitle: 'Upload',
      phaseLabel: 'Batch · collecting',
      kind: 'io',
      purpose:
        'Creates the batch and keeps every source file. No classifier, size, or facit filter. An optional model is stored on the batch. Bytes go to the same durable blob store as the multi-step pipe.',
      inputs: [
        {
          id: 'upload-files',
          label: 'Source files',
          summary:
            'Each uploaded file. pipeId is ffu-single-call. Files stay queued until the call finishes.',
        },
      ],
      outputs: [
        {
          id: 'upload-rows',
          label: 'Batch + file rows',
          summary:
            'collecting jumps straight to consolidating. Classify and extract are not entered.',
        },
      ],
      col: 0,
      row: 0,
    },
    {
      id: 'read',
      title: 'One consolidation call',
      shortTitle: 'Read',
      phaseLabel: 'Batch · consolidating',
      kind: 'ai',
      purpose:
        'One OpenAI call. Every source file is attached. No drawing search, raster, footprint, segment read, or site model. ' +
        aiCallNote,
      ...modelUseCaseMeta('projectFileLabelConsolidation'),
      inputs: [
        {
          id: 'read-files',
          label: 'All source files',
          summary:
            'Original bytes for every file on the run. A missing blob fails the batch.',
        },
      ],
      outputs: [
        {
          id: 'read-ai',
          label: 'Consolidation structured output',
          summary:
            'Same consolidation schema as the multi-step merge. extractedDesignFacts is passed through toFfuOutputFacts with no drawings and no site model.',
          schemas: [
            {
              id: 'consolidation',
              label: 'project_file_batch_consolidation',
              structuredOutputName:
                PROJECT_FILE_BATCH_MERGE_STRUCTURED_OUTPUT_NAME,
              schema,
            },
          ],
        },
      ],
      col: 1,
      row: 0,
    },
    {
      id: 'output',
      title: 'Final output',
      shortTitle: 'Output',
      phaseLabel: 'Batch · consolidating',
      kind: 'code',
      purpose:
        'Writes the aggregated consolidation, then finalOutput via toFinalFfuOutputFacts when those facts parse as FfuOutputFacts. Logs AI usage by step. No entrance-index layout and no site model — those run only on the multi-step pipe.',
      inputs: [
        {
          id: 'output-in',
          label: 'Consolidation answer',
          summary:
            'Suggested labels, buildings, compositions, and extractedDesignFacts from the one call.',
        },
      ],
      outputs: [
        {
          id: 'output-facts',
          label: 'finalOutput',
          summary:
            'Design-normalised FfuOutputFacts on the batch, beside the aggregated object.',
          schemas: [
            {
              id: 'ffu-output-facts',
              label: 'FfuOutputFacts',
              schema: zodToJsonSchema(ffuOutputFactsSchema),
            },
          ],
        },
      ],
      col: 2,
      row: 0,
    },
    {
      id: 'done',
      title: 'Batch done',
      shortTitle: 'Done',
      phaseLabel: 'Batch · done',
      kind: 'io',
      purpose:
        'The batch stores both outputs. Readers of aggregated output and of finalOutput read this batch the same way as a multi-step batch. pipeId stays ffu-single-call. createProject uses finalOutput.',
      inputs: [
        {
          id: 'done-in',
          label: 'Aggregated output + finalOutput',
          summary:
            'Queued files are marked done with the batch. A failed call marks them failed.',
        },
      ],
      outputs: [
        {
          id: 'done-out',
          label: 'Snapshot',
          summary: 'pipeId stays ffu-single-call on the batch.',
        },
      ],
      col: 3,
      row: 0,
    },
  ];
  return {
    nodes,
    edges: [
      { from: 'upload', to: 'read', kind: 'next' },
      { from: 'read', to: 'output', kind: 'next' },
      { from: 'output', to: 'done', kind: 'next' },
    ],
    lanes: [],
  };
};

export const getProjectFilePipeFlowGraph = (
  pipeId: ProjectFilePipeId,
): PipelineFlowGraph => {
  switch (pipeId) {
    case FFU_PIPE_ID:
      return getProjectFilePipelineFlowGraph();
    case FFU_SINGLE_CALL_PIPE_ID:
      return getSingleCallPipeFlowGraph();
    default:
      return assertUnreachable(pipeId);
  }
};

/** Schema property count for expander captions. Nested $defs are ignored. */
export const countJsonSchemaProperties = (
  schema: Record<string, unknown>,
): number => {
  const props = schema.properties;
  if (!guards.is.object(props)) return 0;
  return Object.keys(props).length;
};
